import { SportType, SportObjectType, MonetizationType, PresentationType } from '@/@types/graphql-types';
import type { SportsSqtvOffer } from '@/features/buybox/types/types';
import { computed } from 'vue';
import { toValue, type MaybeRefOrGetter } from '@vueuse/core';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { titleHasTag, titleHasSubgenre, titleHasGenre } from '@/helpers/ad/useTitleSqtvTargeting';
import { useI18n } from '@/helpers/composables/useI18n';
import adminConfig from '@/features/promotions/config.json';

// Title must have genre 'spt' or one of these subgenres (narrower than targetingSubgenres — excludes 'rltc') to
// qualify for the generic "Sports" fallback tag below.
const SPORTS_TAG_GENRES = ['spt'];
const SPORTS_TAG_SUBGENRES = ['cosc', 'sprd'];
// If the title already carries this tag directly, it'll already surface via a campaign's displayTags match —
// no need for the generic "Sports" fallback on top.
const EXPLICIT_SPORTS_TAGS = ['sports'];

// Tags appropriate to show as a badge next to F1 (e.g. "motorcycle_race" makes sense, "baseball" doesn't) —
// also included in the F1 campaign's targeting criteria below, so it's written once here instead of twice.
const F1_DISPLAY_TAGS = [
	'extreme_sport',
	'sports',
	'championship',
	'contests_and_competitions',
	'rivalry',
	'motorcycle_race',
	'car_race',
	'team',
	'training',
	'stadium',
];
// Additional tags that should gate the F1 campaign's targeting but aren't appropriate to show as a badge.
const F1_ADDITIONAL_TARGETING_TAGS = [
	'athlete_and_trainer',
	'athletes_life',
	'baseball',
	'basketball',
	'boxing',
	'ex_athlete',
	'football',
	'golf',
	'hockey',
	'rivals',
	'soccer',
	'sport',
	'sport_team',
	'tennis',
	'wrestling',
	'uninhibited_rivalry',
	'underdog',
	'cycling',
	'running',
	'surfing',
	'swimming',
	'coach',
	'cheerleader',
	'basic_training',
	'major_league',
	'comeback',
	'skiing',
];

export interface SportsSqtvCampaignConfig {
	id: string;
	countries: string[];
	providerShortName: string;
	sportType: SportType;
	sportObjectType?: SportObjectType;
	/** sport_competition.id, sport_season.id, sport_event.id, sport_venue.id — set on SportContext when present. */
	competitionId?: number;
	seasonId?: number;
	eventId?: number;
	venueId?: number;
	title: string;
	/** Event name only, no brand prefix or year (e.g. "Dutch Grand Prix") — used to build the adapted title alongside a superscripted ™/® mark in SportsSingleQTV.vue, so the mark doesn't have to live inside this string. */
	raceName?: string;
	/** Event year (e.g. "2026"), appended after `raceName`'s trademark mark in SportsSingleQTV.vue. */
	raceYear?: string;
	/** Base path with no extension — expects `${posterUrl}.avif` and `${posterUrl}.jpg` in the public folder. */
	posterUrl: string;
	/** Base path with no extension — expects `${backdropUrl}.avif` and `${backdropUrl}.jpg` in the public folder. */
	backdropUrl: string;
	bannerText: string;
	disclaimerText?: string;
	/** Subset of tag technicalNames appropriate to show as a badge on the SQTV card for this campaign — narrower than `targetingTags`, which only gates whether the campaign shows at all. */
	displayTags?: string[];
	offer: SportsSqtvOffer;
	/** Title must have at least one of these tags, subgenres, or a matching genre to be targeted. If none of the three are set, only `countries` gates the campaign. */
	targetingTags?: string[];
	targetingSubgenres?: string[];
	targetingGenres?: string[];
	/** Additional package shortNames to exclude — alongside `providerShortName` itself — if the title already has an offer on one of them (no need to cross-promote). */
	excludedPackages?: string[];
}

// Hardcoded sports SQTV campaigns. Clickout only, no scoring (see SingleQTV's `sportsTargetEvent` prop).
// First matching entry (country + title targeting) takes priority.
export const SPORTS_SQTV_CAMPAIGNS: SportsSqtvCampaignConfig[] = [
	{
		id: 'f1-monza-gp-atv',
		countries: ['US'],
		providerShortName: 'af1',
		sportType: SportType.FormulaRacing,
		sportObjectType: SportObjectType.MainEvent,
		competitionId: 1422294,
		seasonId: 52760339,
		eventId: 54624588, // -> Spanish Grand Prix next week 54847880
		venueId: 1753387, // --> Spanish Grand Prix next week 43504492
		title: "Formula 1® Pirelli Gran Premio D'Italia™ 2026",
		raceName: "Pirelli Gran Premio D'Italia™",
		raceYear: '2026',
		posterUrl: 'https://www.justwatch.com/appassets/img/f1_atp_poster',
		backdropUrl: 'https://www.justwatch.com/appassets/img/f1_atp_banner',
		bannerText: 'Watch similar events on Apple TV with a free trial!',
		disclaimerText:
			'Redeem your 7 day free trial. New subscribers only. $14.99/mo after trial. Auto-renews until cancelled. Terms apply.',
		displayTags: F1_DISPLAY_TAGS,
		offer: {
			package: {
				packageId: 2677,
				clearName: 'F1 on Apple TV',
				technicalName: 'f1appletv',
				shortName: 'af1',
				iconWide: '/icon_wide/338253918/s160/appletvplus.{format}',
			},
			monetizationType: MonetizationType.Flatrate,
			presentationType: PresentationType.Hd,
			standardWebURL:
				'https://tv.apple.com/channel/tvs.sbd.241000?itscg=30200&itsct=justwatch_formula_1&at=1000l3V2',
		},
		targetingTags: [...F1_DISPLAY_TAGS, ...F1_ADDITIONAL_TARGETING_TAGS],
		targetingSubgenres: ['rltc', 'cosc', 'sprd', 'acat'],
		targetingGenres: ['spt'],
		excludedPackages: ['atp'],
	},
];

function campaignMatchesTitle(config: SportsSqtvCampaignConfig, title: TitleDetail): boolean {
	// Title already available on the target provider (or one of the additionally excluded packages) — no need to cross-promote.
	if (
		title.offers.some(
			o =>
				o.package.shortName === config.providerShortName ||
				config.excludedPackages?.includes(o.package.shortName ?? '')
		)
	)
		return false;

	const hasTargetingCriteria = !!(
		config.targetingTags?.length ||
		config.targetingSubgenres?.length ||
		config.targetingGenres?.length
	);
	// No targeting criteria configured — campaign is gated by country alone.
	if (!hasTargetingCriteria) return true;

	return (
		(!!config.targetingTags?.length && titleHasTag(title, config.targetingTags)) ||
		(!!config.targetingSubgenres?.length && titleHasSubgenre(title, config.targetingSubgenres)) ||
		(!!config.targetingGenres?.length && titleHasGenre(title, config.targetingGenres))
	);
}

function getSportsSqtvCampaign(country: string, title: TitleDetail): SportsSqtvCampaignConfig | null {
	if (!adminConfig.steering_overrides_us.sports_sqtv_atp) return null;

	for (const config of SPORTS_SQTV_CAMPAIGNS) {
		if (!config.countries.includes(country)) continue;
		if (!campaignMatchesTitle(config, title)) continue;
		return config;
	}
	return null;
}

export function useSportsSqtvCampaign(country: MaybeRefOrGetter<string>, title: MaybeRefOrGetter<TitleDetail>) {
	const { tc } = useI18n();
	const _title = computed(() => toValue(title));
	const campaign = computed(() => getSportsSqtvCampaign(toValue(country), _title.value));

	/*
	 * Sports SQTV tags -> mirrors useSingleQTV's getSQTVTags (max 3, prefer specific over generic).
	 *
	 * Prio order (max 3, min 1)
	 * 1. Campaign's displayTags whitelist matched against the title's own tags
	 * 2. "Free Trial" (if the campaign's offer is Flatrate and the title itself has a Flatrate offer with a trial plan)
	 * 3. "Sports" (if title has genre 'spt' or subgenre 'cosc'/'sprd', and doesn't already carry an explicit
	 *    sports tag — which would already be covered by #1)
	 * 4. "Similar" (generic fallback)
	 */
	const tags = computed(() => {
		const config = campaign.value;
		if (!config) return undefined;

		// Tags on title
		const whitelist = config.displayTags ?? [];
		const titleTags = _title.value.content.tags ?? [];
		const matchedTags = titleTags
			.filter(t => whitelist.includes(t.technicalName))
			.map(t => t.translatedName)
			.slice(0, 3);

		// Sports tag
		if (matchedTags.length < 3) {
			const hasSportsGenreOrSubgenre =
				titleHasGenre(_title.value, SPORTS_TAG_GENRES) || titleHasSubgenre(_title.value, SPORTS_TAG_SUBGENRES);
			const hasExplicitSportsTag = titleHasTag(_title.value, EXPLICIT_SPORTS_TAGS);
			if (hasSportsGenreOrSubgenre && !hasExplicitSportsTag) matchedTags.push(tc('WEBAPP_TAG_SPORTS'));
		}

		// Free Trial Tag
		if (matchedTags.length < 3 && config.offer.monetizationType === MonetizationType.Flatrate) {
			const hasFreeTrialOffer = _title.value.offers?.some(
				o =>
					o.monetizationType === MonetizationType.Flatrate &&
					(o.package.planOffers?.some(p => p.isTrial || (p.children?.length ?? 0) > 0) ?? false)
			);

			if (hasFreeTrialOffer) matchedTags.push(tc('WEBAPP_FREE_TRIAL_TAG'));
		}

		// Similar Tag
		if (matchedTags.length < 3) matchedTags.push(tc('WEBAPP_SIMILAR'));

		return matchedTags;
	});

	return { campaign, tags };
}
